import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { plyxApi } from "@/lib/plyxApi";
import { isPlyxStandaloneMode } from "@/lib/plyxStandalone";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PlyxRegister = () => {
  const [form, setForm] = useState({
    handle: "",
    display_name: "",
    email: "",
    phone: "",
    country: "",
    password: "",
  });
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loading, setLoading] = useState(false);
  const { register } = usePlyxAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    plyxApi.get("/plyx/auth/countries")
      .then((r) => { if (alive) setCountries(r.data?.countries || []); })
      .catch(() => { /* dropdown becomes empty, error surfaces on submit */ })
      .finally(() => { if (alive) setLoadingCountries(false); });
    return () => { alive = false; };
  }, []);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.iso === form.country),
    [countries, form.country]
  );

  const onChange = (k) => (e) => {
    const v = k === "handle"
      ? e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "")
      : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country) {
      toast.error("Please choose your country");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Add an email or phone number to sign up");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        handle: form.handle,
        display_name: form.display_name,
        country: form.country,
        password: form.password,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      await register(payload);
      toast.success(`Welcome to Plyx, @${form.handle}`);
      navigate("/plyx/feed", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-16" data-testid="plyx-register-page">
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Join Plyx</h1>
        <p className="text-white/60 mb-8">
          {isPlyxStandaloneMode()
            ? "Create your Plyx account — start watching, posting and earning in minutes."
            : "A separate Plyx account — you don't need AfriMarket. Link it later if you do."}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Handle</label>
            <div className="flex items-center bg-white/8 border border-white/15 rounded-md focus-within:ring-2 focus-within:ring-[#FF2D7A]/50">
              <span className="pl-3 text-white/50">@</span>
              <input
                required minLength={3} maxLength={24}
                value={form.handle}
                onChange={onChange("handle")}
                data-testid="plyx-register-handle"
                className="plyx-input flex-1 px-2 py-3 bg-transparent focus:outline-none"
                placeholder="yourname"
              />
            </div>
            <div className="text-xs text-white/40 mt-1">3–24 letters, numbers or underscores.</div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Display name</label>
            <input
              required maxLength={60}
              value={form.display_name}
              onChange={onChange("display_name")}
              data-testid="plyx-register-display-name"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">
              Country <span className="text-[#FF2D7A]">*</span>
            </label>
            <select
              required
              value={form.country}
              onChange={onChange("country")}
              data-testid="plyx-register-country"
              disabled={loadingCountries}
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50 disabled:opacity-60"
            >
              <option value="" disabled>
                {loadingCountries ? "Loading countries…" : "Select your country"}
              </option>
              {countries.map((c) => (
                <option key={c.iso} value={c.iso} className="bg-[#0A0A0A]">
                  {c.name} ({c.dial_code})
                </option>
              ))}
            </select>
            <div className="text-xs text-white/40 mt-1">
              Sets your dialling code automatically when you add a phone number.
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={onChange("email")}
              data-testid="plyx-register-email"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Phone number</label>
            <div className="flex items-stretch gap-2">
              <div
                data-testid="plyx-register-dial-code"
                className="px-3 py-3 rounded-md bg-white/5 border border-white/15 text-sm text-white/70 min-w-[64px] flex items-center justify-center"
              >
                {selectedCountry?.dial_code || "+—"}
              </div>
              <input
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={onChange("phone")}
                data-testid="plyx-register-phone"
                placeholder="National number"
                className="plyx-input flex-1 px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
              />
            </div>
            <div className="text-xs text-white/40 mt-1">
              Add email <span className="text-white/55">or</span> phone — you can log in with either.
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Password</label>
            <input
              type="password" required minLength={6}
              value={form.password}
              onChange={onChange("password")}
              data-testid="plyx-register-password"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="plyx-register-submit"
            className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "#E85D4E" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create Plyx account"}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-white/60">
          Already on Plyx?{" "}
          <Link to="/plyx/login" data-testid="plyx-register-to-login" className="underline hover:text-white">Log in</Link>
        </div>
      </div>
    </div>
  );
};

export default PlyxRegister;
